import { Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../../environments/environment';
import { API } from '../../API';
import { ApiService } from '../api-service/api-service';

export interface NotificationItem {
  message_fed_seq: number;
  event_key: string;
  channel: string;
  status: string;
  body: string;
  subject: string | null;
  // Original producer-supplied identifiers (entity_type, demande_fed_seq,
  // club_fed_seq, player_fed_seq, etc. — whatever that event's payload carries),
  // added 2026-08-06 so a notification can navigate to its own detail page instead
  // of just displaying text. Shape varies per event_key — see
  // NotificationRouteService for how each one is interpreted.
  payload: Record<string, any>;
  read_at: string | null;
  created_at: string;
}

/**
 * Real-time in-app notifications — Socket.io connects with the same auth_token
 * HttpOnly cookie the REST API already uses (withCredentials: true), verified
 * server-side in sockets/notificationSocket.js via the identical per-user-secret
 * JWT scheme passport.js uses. Falls back to nothing silently if the handshake
 * fails (e.g. not logged in) — this service is only meaningfully used from inside
 * the authenticated layout. REST calls still go through ApiService, same as
 * everywhere else in this project — only the socket connection itself (not
 * HttpClient-based) needs its own explicit withCredentials.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private socket: Socket | null = null;

  notifications = signal<NotificationItem[]>([]);
  unreadCount = signal(0);

  constructor(private api: ApiService) {}

  connect(): void {
    if (this.socket) return; // already connected — safe to call from multiple bell instances

    this.socket = io(environment.apiUrl, { withCredentials: true });
    this.socket.on('notification', () => {
      // Payload itself isn't trusted as the source of truth for the list (it lacks
      // message_fed_seq needed for mark-read) — a push just means "something's new",
      // so re-fetch the real rows instead of hand-assembling one from the socket event.
      this.loadRecent();
      this.loadUnreadCount();
    });

    this.loadRecent();
    this.loadUnreadCount();
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  loadRecent(): void {
    this.api.get(API.NOTIFICATIONS.LIST).subscribe({
      next: (res) => this.notifications.set(res?.data || []),
      error: () => {},
    });
  }

  loadUnreadCount(): void {
    this.api.get(API.NOTIFICATIONS.UNREAD_COUNT).subscribe({
      next: (res) => this.unreadCount.set(res?.data?.unreadCount || 0),
      error: () => {},
    });
  }

  markRead(fedSeq: number): void {
    const path = API.NOTIFICATIONS.MARK_READ.replace(':fedSeq', String(fedSeq));
    this.api.patch(path, {}).subscribe({
      next: () => {
        this.notifications.update(list => list.map(n => n.message_fed_seq === fedSeq ? { ...n, read_at: new Date().toISOString() } : n));
        this.unreadCount.update(c => Math.max(0, c - 1));
      },
      error: () => {},
    });
  }

  markAllRead(): void {
    this.api.patch(API.NOTIFICATIONS.MARK_ALL_READ, {}).subscribe({
      next: () => {
        this.notifications.update(list => list.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
        this.unreadCount.set(0);
      },
      error: () => {},
    });
  }
}
